/**
 * MorningBriefing — auto-refreshing daily playbook. Each new calendar day it
 * refreshes to today's top 3 moves; managers open to a ready checklist and can
 * push it to store managers. Checking a move creates a tracked task (onAssign).
 */
import React, { useEffect, useState } from 'react';
import { Sunrise, Bell, Check, Circle, ArrowRight, Send, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { money } from './qsrDemoData';

const KEY = (bid) => `qsr_briefing_${bid || 'demo'}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function MorningBriefing({ actions = [], businessId, onAssign, isAssigned, onSelectStore }) {
  const [pushed, setPushed] = useState(false);
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem(KEY(businessId))) || {}; } catch { stored = {}; }
    if (stored.date !== today()) { setIsNew(true); setPushed(false); }
    else { setIsNew(false); setPushed(!!stored.pushed); }
  }, [businessId]);

  const push = () => {
    try { localStorage.setItem(KEY(businessId), JSON.stringify({ date: today(), pushed: true })); } catch { /* ignore */ }
    setPushed(true); setIsNew(false);
    toast.success('Briefing pushed', { description: "Today's top 3 moves sent to your store managers." });
  };

  const assignAll = () => {
    const pending = actions.filter((a) => !isAssigned?.(a.id));
    pending.forEach((a) => onAssign?.(a));
    if (pending.length) toast.success('Added to today', { description: `${pending.length} move${pending.length > 1 ? 's' : ''} assigned to store managers.` });
  };

  const pushRoster = () => {
    toast.success('Sent to Tanda', { description: "Today's moves + recommended crew pushed to rostering. (Demo — connect Tanda to go live.)" });
  };

  const dateLabel = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' });

  return (
    <div className="qsr-card rounded-3xl p-5 sm:p-6" data-testid="morning-briefing">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-amber-600 text-xs font-semibold">
            <Sunrise className="w-4 h-4" /> Morning briefing
            {isNew && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid="briefing-fresh">
                <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" /></span>
                Refreshed this morning
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mt-1">Today's top 3 moves</h3>
          <p className="text-xs text-slate-500 mt-0.5">{dateLabel} · auto-refreshes 6:00 AM daily</p>
        </div>
        <button
          onClick={push}
          disabled={pushed}
          className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold px-3 py-2 transition-colors ${pushed ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default' : 'bg-amber-500 text-white hover:bg-amber-600'}`}
          data-testid="briefing-push"
        >
          {pushed ? <><Check className="w-3.5 h-3.5" /> Pushed to managers</> : <><Send className="w-3.5 h-3.5" /> Push to managers</>}
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {actions.map((a, i) => {
          const done = isAssigned?.(a.id);
          return (
            <div key={a.id} className="flex items-center gap-3 rounded-2xl border border-slate-200/70 bg-white/60 p-3" data-testid={`briefing-move-${a.id}`}>
              <button
                onClick={() => !done && onAssign?.(a)}
                className={`shrink-0 ${done ? 'text-emerald-600' : 'text-slate-300 hover:text-slate-500'}`}
                data-testid={`briefing-check-${a.id}`}
                aria-label={done ? 'assigned' : 'assign'}
              >
                {done ? <Check className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-amber-600">#{i + 1}</span>
                  <button onClick={() => onSelectStore?.(a.store)} className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 hover:underline underline-offset-2">{a.store.name}</button>
                  {a.impact > 0 && <span className="text-[11px] font-semibold text-emerald-600">+{money(a.impact)}/wk</span>}
                </div>
                <div className={`text-sm font-medium mt-0.5 ${done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{a.title}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Bell className="w-3 h-3" /> Managers get this as a push each morning</span>
        <div className="flex items-center gap-3">
          <button onClick={pushRoster} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-900" data-testid="briefing-roster-push">
            <CalendarClock className="w-3.5 h-3.5" /> Push to Tanda
          </button>
          <button onClick={assignAll} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900" data-testid="briefing-assign-all">
            Assign all 3 <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
